import { Row } from "antd";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import M from "antd/lib/modal/Modal";
import { color } from "src/styles/globals";
import styled, { css } from "styled-components";
import { Title as sundayTitle, Body as sundayBody } from "sunday-component";
import CommonButton from "./Button";
import ICTHButton from "./ICTH/Button";
import { ModalBodyText, ModalTitle } from "./ICTH/Typography";

type DialogAppearance = "ice-cream";

const Title = styled(sundayTitle).attrs({ level: 5 })`
  margin-top: 8px;
  text-align: center;
  color: ${color.charcoal10};
`;
const Body = styled(sundayBody).attrs({ level: 7 })`
  margin-top: 8px;
  text-align: center;
  color: ${color.charcoal10};
`;

const IceCreamTitle = styled(ModalTitle)`
  && {
    --typography-color: #202123;

    margin: 16px auto 0;
    color: #202123;
    white-space: pre-line;
  }
`;

const IceCreamBody = styled(ModalBodyText)`
  && {
    margin: 8px auto 0;
  }
`;

const ButtonGroup = styled.div<{ $appearance?: DialogAppearance }>`
  margin-top: ${({ $appearance }) =>
    $appearance === "ice-cream" ? "24px" : "16px"};

  ${({ $appearance }) =>
    $appearance === "ice-cream" &&
    css`
      display: flex;
      flex-direction: column;
      gap: 8px;
    `}
`;

const Button = styled(CommonButton).attrs({
  fullWidth: true,
})`
  margin-top: 8px;
  p,
  div {
    font-size: 21px;
  }
`;
const IceCreamButton = styled(ICTHButton).attrs({
  fullWidth: true,
})`
  &&& {
    height: 48px;
    margin-top: 0;
    border-radius: 999px;
    box-shadow: none;
    font-family: var(--typescale-font-th, "Noto Sans Thai"), sans-serif !important;
    font-size: 15px !important;
    font-weight: 700 !important;
    line-height: 24px !important;

    &.sunday-component-btn-default:not(:disabled) {
      border-color: var(--color-stroke-default, #d9dbde);
      background: #ffffff;
      box-shadow: none;
    }

    > span,
    * {
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
    }
  }
`;

const Modal = styled(M)<{ $appearance?: DialogAppearance }>`
  .ant-modal-content {
    border-radius: 12px;
  }
  .ant-modal-header {
    border-radius: 12px;
  }

  ${({ $appearance }) =>
    $appearance === "ice-cream" &&
    css`
      max-width: calc(100vw - 32px);
      font-family: var(--typescale-font-th, "Noto Sans Thai"), sans-serif;

      &.ant-modal {
        width: 303px !important;
      }

      .ant-modal-content {
        border-radius: 4px;
        box-shadow: 0 8px 24px rgba(32, 33, 35, 0.16);
      }

      .ant-modal-body {
        padding: 24px;
      }
    `}
`;
const Image = styled.img`
  width: 56px;
`;

export type ErrorDialogProps = {
  title: string;
  description: string | JSX.Element;
  errorButtons?: Array<{ text: string; onClick: Function; type?: string }>;
  isVisible?: boolean;
  icon?: JSX.Element;
  width?: string;
  appearance?: DialogAppearance;
  className?: string;
};

const ErrorDialog = ({
  title,
  description,
  isVisible = true,
  errorButtons = [],
  icon,
  width,
  appearance,
  className,
}: ErrorDialogProps) => {
  const { sm } = useBreakpoint();
  const isIceCream = appearance === "ice-cream";
  const defaultWidth = sm || isIceCream ? "303px" : "auto";
  const DialogButton = isIceCream ? IceCreamButton : Button;
  const renderedIcon = icon || (
    <Image src="/static/common/x-circle-filled.svg" alt="x-circle" />
  );
  let renderedDescription = description;
  if (typeof description === "string") {
    renderedDescription = isIceCream ? (
      <IceCreamBody>{description}</IceCreamBody>
    ) : (
      <Body>{description}</Body>
    );
  }

  return (
    <Modal
      className={className}
      $appearance={appearance}
      centered={isIceCream}
      width={width ?? defaultWidth}
      visible={isVisible}
      footer={null}
      closable={false}
      data-testid="error-dialog-modal"
      data-gtm-input-code="error-dialog-modal"
    >
      <Row justify="center">{renderedIcon}</Row>
      {isIceCream ? (
        <IceCreamTitle>{title}</IceCreamTitle>
      ) : (
        <Title>{title}</Title>
      )}
      {renderedDescription}
      {errorButtons.length > 0 && (
        <ButtonGroup $appearance={appearance}>
          {errorButtons.map(({ text, onClick, type = "primary" }, index) => (
            <DialogButton
              key={text}
              data-testid={`error-dialog-button-${index}`}
              data-gtm-input-code={`error-dialog-button-${index}`}
              onClick={onClick}
              type={type}
            >
              {text}
            </DialogButton>
          ))}
        </ButtonGroup>
      )}
    </Modal>
  );
};
export default ErrorDialog;
